import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, Chip, Collapse, Icon, IconButton, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { DateFormatter } from 'components/AppTable/CellsComponents';
import { getUserCompliance } from 'http/compliance';

const TRAINING_METHODS = ['theory', 'practical', 'ojt'];

const useStyles = makeStyles(theme => ({
  moduleBlock: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[50],
  },
  partBlock: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.common.white,
  },
  metaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  detailLine: {
    marginTop: theme.spacing(0.5),
  },
  sectionTitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
  },
  roleHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    userSelect: 'none',
  },
  roleHeaderMain: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    flex: 1,
    minWidth: 0,
  },
}));

const statusColor = status => {
  switch (status) {
    case 'valid':
      return 'primary';
    case 'expiring_soon':
      return 'default';
    case 'expired':
      return 'secondary';
    default:
      return 'default';
  }
};

const formatDateTime = value => {
  if (!value) {
    return '—';
  }
  return <DateFormatter value={value} format="MM/DD/YYYY LT" />;
};

const ComplianceDetails = ({ userId, refreshVersion }) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedByJobTaskId, setExpandedByJobTaskId] = useState({});

  const trainingMethodLabel = method => {
    if (TRAINING_METHODS.includes(method)) {
      return t(`trainingModules.tabs.${method}`);
    }
    return method || '—';
  };

  const toggleRoleExpanded = jobTaskId => {
    setExpandedByJobTaskId(prev => ({
      ...prev,
      [jobTaskId]: !(prev[jobTaskId] ?? false),
    }));
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getUserCompliance(userId)
      .then(response => {
        if (mounted) {
          const nextItems = response.items || [];
          setItems(nextItems);
          setExpandedByJobTaskId(prev => {
            const next = { ...prev };
            nextItems.forEach((item, index) => {
              if (next[item.jobTaskId] === undefined) {
                next[item.jobTaskId] = index === 0;
              }
            });
            return next;
          });
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [userId, refreshVersion]);

  if (loading) {
    return <Typography>{t('loading')}</Typography>;
  }

  if (!items.length) {
    return <Typography>{t('compliance.noData')}</Typography>;
  }

  return (
    <Box>
      {items.map(item => {
        const isExpanded = expandedByJobTaskId[item.jobTaskId] ?? false;

        return (
          <Card key={`${item.userId}-${item.jobTaskId}`} style={{ marginBottom: 16 }}>
            <CardContent>
              <Box
                className={classes.roleHeader}
                onClick={() => toggleRoleExpanded(item.jobTaskId)}
                role="button"
                tabIndex={0}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleRoleExpanded(item.jobTaskId);
                  }
                }}
              >
                <Box className={classes.roleHeaderMain}>
                  <IconButton
                    size="small"
                    aria-label={isExpanded ? t('compliance.collapseRole') : t('compliance.expandRole')}
                    onClick={event => {
                      event.stopPropagation();
                      toggleRoleExpanded(item.jobTaskId);
                    }}
                  >
                    {isExpanded ? <Icon>expand_less</Icon> : <Icon>expand_more</Icon>}
                  </IconButton>
                  <Typography variant="h6">{item.jobTaskName}</Typography>
                  <Chip label={item.status} color={statusColor(item.status)} size="small" />
                  {!isExpanded ? (
                    <Typography variant="body2" color="textSecondary">
                      {t('compliance.version')}: {item.jobTaskVersionCode || '—'}
                    </Typography>
                  ) : null}
                </Box>
              </Box>

              <Collapse in={isExpanded}>
                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {t('compliance.version')}: {item.jobTaskVersionCode || '—'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {t('compliance.evaluatedAt')}: {formatDateTime(item.evaluatedAt)}
                  </Typography>
                  {item.causes?.length > 0 && (
                    <Box mb={2}>
                      {item.causes.map(cause => (
                        <Typography key={cause} variant="body2">
                          {cause}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {(item.trainingModules || []).map(module => (
                    <Box key={module.trainingModuleId} className={classes.moduleBlock}>
                      <Box className={classes.metaRow}>
                        <Typography variant="subtitle1">
                          {module.trainingModuleName} ({module.trainingModuleCode})
                        </Typography>
                        <Chip label={module.status} color={statusColor(module.status)} size="small" />
                      </Box>

                      <Typography variant="body2" className={classes.sectionTitle}>
                        {t('compliance.achievement')}
                      </Typography>
                      {module.achievement ? (
                        <>
                          <Typography variant="body2" className={classes.detailLine}>
                            {t('compliance.issuedAt')}: {formatDateTime(module.achievement.achievedAt)}
                          </Typography>
                          <Typography variant="body2" className={classes.detailLine}>
                            {t('compliance.expiresAt')}: {formatDateTime(module.achievement.expiresAt)}
                          </Typography>
                          <Typography variant="body2" className={classes.detailLine}>
                            {t('compliance.achievementStatus')}: {module.achievement.status}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          {t('compliance.noAchievement')}
                        </Typography>
                      )}

                      {(module.trainingParts || []).map(part => (
                        <Box key={part.trainingPartId} className={classes.partBlock}>
                          <Box className={classes.metaRow}>
                            <Typography variant="body2">
                              {part.trainingPartCode} {part.trainingPartVersion}
                            </Typography>
                            <Chip label={trainingMethodLabel(part.trainingMethod)} size="small" variant="outlined" />
                            <Chip
                              label={part.satisfied ? t('compliance.satisfied') : t('compliance.missing')}
                              color={part.satisfied ? 'primary' : 'default'}
                              size="small"
                            />
                          </Box>

                          {part.usedPreviousPart ? (
                            <Typography variant="body2" color="textSecondary" className={classes.detailLine}>
                              {t('compliance.usedPreviousPart')}
                              {part.transitionPolicyApplied
                                ? ` (${t('compliance.transitionPolicy')}: ${part.transitionPolicyApplied})`
                                : ''}
                            </Typography>
                          ) : null}

                          <Typography variant="body2" className={classes.sectionTitle}>
                            {t('compliance.evidence')}
                          </Typography>
                          {part.evidence ? (
                            <>
                              <Typography variant="body2" className={classes.detailLine}>
                                {t('compliance.issuedAt')}: {formatDateTime(part.evidence.issuedAt)}
                              </Typography>
                              <Typography variant="body2" className={classes.detailLine}>
                                {t('compliance.expiresAt')}: {formatDateTime(part.evidence.expiresAt)}
                              </Typography>
                              <Typography variant="body2" className={classes.detailLine}>
                                {t('compliance.approvalStatus')}: {part.evidence.approvalStatus}
                              </Typography>
                              {part.daysUntilExpiry != null ? (
                                <Typography variant="body2" className={classes.detailLine}>
                                  {t('compliance.daysUntilExpiry')}: {part.daysUntilExpiry}
                                </Typography>
                              ) : null}
                            </>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              {t('compliance.noEvidence')}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

ComplianceDetails.propTypes = {
  userId: PropTypes.number.isRequired,
  refreshVersion: PropTypes.number,
};

ComplianceDetails.defaultProps = {
  refreshVersion: 0,
};

export default ComplianceDetails;
